/**
 * ChangePassword Component
 *
 * Description:
 * This component provides a form for users to change their password. It includes fields for the current password, new password,
 * and confirm password. It performs validation to ensure that the new and confirm passwords match. On submission, it makes an
 * API call to update the password and displays appropriate success or error messages based on the response.
 *
 * Props: None
 *
 * File Name: ChangePassword.tsx
 * Date: 10-08-2024
 *
 * Marker: DOC_START
 */

import React, { useState } from "react";
import SimpleInput from "../SimpleInput";
import PrimaryBtn from "../GlobalComponents/primaryBtn";
import { useForm, Controller } from "react-hook-form";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import { removeToken } from "src/helpers/helperFunctions";
import { useDispatch } from "react-redux";
import { AppDispatch } from "src/Store/store";
import { removeAuth } from "src/Store/reducer";
import { Link, useNavigate } from "react-router-dom";
import tickIcon from "../../assets/images/checked-icon-modal.png";
import { removeAllIndicators } from "src/Store/redDots";

interface FormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
} // Define the shape of the form values

interface iAPIResponse {
  message: string;
  [key: string]: any;
} // Define the shape of the API response

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [isLoader, setIsLoader] = useState<boolean>(false); // loader state
  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  // State for password, new password & confirm password
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  }); // State for handling form input and validation

  // Custom validation function to ensure new and confirm passwords match
  const validatePasswordMatch = (value: string) => {
    const { newPassword } = getValues();
    return value === newPassword || "Passwords do not match";
  };

  // API_CALL: API call for customer change password
  const onSubmit = async (data: FormValues) => {
    setIsLoader(true);
    const { confirmPassword, ...payload } = data;
    const { res, err } = await httpRequest<iAPIResponse>({
      method: "put",
      path: "/customer/change-password",
      params: payload,
    });
    if (res) {
      setIsModal(true);
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };
  const [isModal, setIsModal] = useState<boolean>(false);
  return (
    <div className="parent-box parent-box-docter-margin">
      <h2 className="title-heading font-Poppins-SemiBold font-size-20px pb-5">
        Change Password
      </h2>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="wellcome-login-admin flex flex-col"
      >
        <div>
          <div className="mb-2">
            <Controller
              name="currentPassword"
              control={control}
              rules={{ required: "Password is required" }}
              render={({ field }) => (
                <SimpleInput
                  isLabel={true}
                  labelText="Current Password"
                  showPasswordEye={true}
                  type={
                    passwordVisibility["currentPassword"] ? "text" : "password"
                  }
                  tooglePasswordVisibility={() =>
                    setPasswordVisibility((prev) => ({
                      ...prev,
                      currentPassword: !prev.currentPassword,
                    }))
                  }
                  extraStyle={{ borderColor: "#00A297", marginBottom: 0 }}
                  id="currentPassword"
                  {...field}
                />
              )}
            />
            {errors.currentPassword && (
              <p className="font-size-14px font-Poppins-Regular text-[#ff0000]">
                {errors.currentPassword.message}
              </p>
            )}
          </div>
          <div className="mb-2">
            <Controller
              name="newPassword"
              control={control}
              rules={{
                required: "New Passowrd is required",
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters long",
                },
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                  message: "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character",
                },
              }}
              render={({ field }) => (
                <SimpleInput
                  isLabel={true}
                  labelText="New Password"
                  showPasswordEye={true}
                  type={passwordVisibility["newPassword"] ? "text" : "password"}
                  tooglePasswordVisibility={() =>
                    setPasswordVisibility((prev) => ({
                      ...prev,
                      newPassword: !prev.newPassword,
                    }))
                  }
                  extraStyle={{ borderColor: "#00A297", marginBottom: 0 }}
                  id="newPassword"
                  {...field}
                />
              )}
            />
            {errors.newPassword && (
              <p className="font-size-14px font-Poppins-Regular text-[#ff0000]">
                {errors.newPassword.message}
              </p>
            )}
          </div>
          <div className="mb-2">
            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: "Confirm Passowrd is required",
                validate: validatePasswordMatch,
              }}
              render={({ field }) => (
                <SimpleInput
                  isLabel={true}
                  labelText="Confirm Password"
                  showPasswordEye={true}
                  type={
                    passwordVisibility["confirmPassword"] ? "text" : "password"
                  }
                  tooglePasswordVisibility={() =>
                    setPasswordVisibility((prev) => ({
                      ...prev,
                      confirmPassword: !prev.confirmPassword,
                    }))
                  }
                  extraStyle={{ borderColor: "#00A297", marginBottom: 0 }}
                  id="confirmPassword"
                  {...field}
                />
              )}
            />
            {errors.confirmPassword && (
              <p className="font-size-14px font-Poppins-Regular text-[#ff0000]">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>
        </div>
        <div>
          <PrimaryBtn
            btnExtralClasses="green-primary-btn theme-bg-green-important"
            loader={isLoader}
            type="submit"
            ButtonText="Update"
          />
        </div>
      </form>
      {isModal && (
        <div className="modal-stripe-overlay">
          <div className="modal-stripe p-6">
            <div className="password-update-success">
              <img src={tickIcon} alt="" />
            </div>
            <h2 className="text-[#2F2F31] leading-tight font-size-40px font-Poppins-SemiBold text-center">
              You have successfully updated your password
            </h2>
            <button
              onClick={() => {
                setValue("currentPassword", "");
                setValue("newPassword", "");
                setValue("confirmPassword", "");
                navigate("/doctor/login");
                dispatch(removeAuth()); // logout
                dispatch(removeAllIndicators());
                removeToken();
                setIsModal(false);
              }}
              id="login-again-btn"
              className="back-to-home-btn text-white font-size-18px font-Poppins-SemiBold"
            >
              Login again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangePassword;
